import { useCallback, useEffect, useRef, useState } from 'react';
import { readMaterialPreview, shootPreviews } from '../materialPreview';

// LE ANTEPRIME SI GENERANO QUANDO LO CHIEDE QUALCUNO.
//
// Non da sole all'apertura: ogni scatto smonta e rimonta l'environment della scena sul thread
// principale, e su una libreria grande sono minuti di lavoro che nessuno ha chiesto. Il
// pannello propone, l'utente decide — e questo hook è il motore che esegue.
//
// UNO ALLA VOLTA, con un respiro fra uno e l'altro: due scatti insieme si calpestano, e senza
// pause la pagina resta a scatti per tutta la durata.

/**
 * L'attesa perché la lista si fermi. Durante il caricamento della scena i materiali compaiono
 * a ondate e le texture arrivano dopo: contarli o fotografarli prima non ha senso.
 */
const SETTLE_DELAY = 1500;

export interface MaterialPreviewProgress {
    done: number;
    total: number;
}

export const useMaterialPreviews = (sceneEditor: any, materials: any[]) => {
    const [previews, setPreviews] = useState<Record<string, string>>({});
    const [progress, setProgress] = useState<MaterialPreviewProgress | null>(null);
    /** La lista si è posata: da qui i conti valgono e la dialog può parlare. */
    const [isSettled, setIsSettled] = useState(false);

    const workingRef = useRef(false);

    // Le anteprime già salvate sulla scena: si leggono dai metadata, costa una lettura.
    useEffect(() => {
        setIsSettled(false);
        if (materials.length === 0) return;

        const timer = setTimeout(() => {
            const saved: Record<string, string> = {};
            materials.forEach((material: any) => {
                const url = material?.id ? readMaterialPreview(sceneEditor, material.id) : null;
                if (url) saved[material.id] = url;
            });

            setPreviews((current) => ({ ...saved, ...current }));
            setIsSettled(true);
        }, SETTLE_DELAY);

        return () => clearTimeout(timer);
    }, [sceneEditor, materials]);

    /**
     * Scatta, carica e appunta — in fila. Torna il numero di anteprime riuscite.
     *
     * Chi non si fotografa si salta: la sua tessera tiene il gradiente, e il conto finale lo
     * dice a chi ha chiesto.
     */
    const generatePreviews = useCallback(
        async (targets: any[]): Promise<number> => {
            const list = targets.filter((material) => material?.id);
            if (!sceneEditor || list.length === 0 || workingRef.current) return 0;

            workingRef.current = true;
            setProgress({ done: 0, total: list.length });

            let generated = 0;
            try {
                // La tessera mostra subito lo scatto, senza aspettare la rete: l'URL caricato serve
                // al prossimo caricamento, non a questo.
                generated = await shootPreviews(sceneEditor, list, (materialID, dataUrl, done, total) => {
                    if (dataUrl) setPreviews((current) => ({ ...current, [materialID]: dataUrl }));
                    setProgress({ done, total });
                });
            } finally {
                workingRef.current = false;
                setProgress(null);
            }

            return generated;
        },
        [sceneEditor]
    );

    /** L'anteprima è arrivata da fuori — il comando «Regenerate» dell'Inspector. */
    const setPreview = useCallback((materialID: string, dataUrl: string) => {
        setPreviews((current) => ({ ...current, [materialID]: dataUrl }));
    }, []);

    const missing = isSettled ? materials.filter((material: any) => material?.id && !previews[material.id]) : [];

    return {
        previews,
        setPreview,
        generatePreviews,
        /** I materiali che non hanno ancora un'anteprima: vuoto finché la lista non si posa. */
        missing,
        progress,
        isGenerating: progress !== null
    };
};
